import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AppWindow, Minus, Maximize2, Minimize2, X } from 'lucide-react';
import { cn } from '../../lib/cn';

const DOCK_ID = 'floating-window-dock';
const MOBILE_BREAKPOINT = 768;
const MIN_WIDTH = 520;
const MIN_HEIGHT = 380;
const EDGE_MARGIN = 12;
const clampPosition = (x: number, y: number, width: number, height: number) => ({
  x: Math.min(Math.max(x, 0), Math.max(0, window.innerWidth - width)),
  y: Math.min(Math.max(y, 0), Math.max(0, window.innerHeight - height))
});

interface Geometry {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface FloatingWindowProps {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  /** Testo mostrato nella pillola quando la finestra è ridotta a icona */
  minimizedLabel?: string;
  /** Chiave per ricordare posizione e dimensione tra una sessione e l'altra */
  storageKey?: string;
  /** Azioni da mostrare nella barra del titolo, prima dei comandi finestra */
  headerActions?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
}

const getDock = (): HTMLElement => {
  let dock = document.getElementById(DOCK_ID);
  if (!dock) {
    dock = document.createElement('div');
    dock.id = DOCK_ID;
    // empty:hidden -> la barra esiste solo quando c'è almeno una finestra ridotta
    dock.className =
      'fixed bottom-16 md:bottom-0 left-0 right-0 z-[79] empty:hidden flex flex-wrap items-center gap-2 border-t border-gray-200 bg-white/95 backdrop-blur px-3 py-2 shadow-[0_-4px_16px_rgba(15,23,42,0.10)]';
    document.body.appendChild(dock);
  }
  return dock;
};

const defaultGeometry = (): Geometry => {
  const width = Math.min(window.innerWidth - EDGE_MARGIN * 2, 1400);
  const height = Math.min(window.innerHeight - EDGE_MARGIN * 2, 940);
  return {
    x: Math.max(EDGE_MARGIN, (window.innerWidth - width) / 2),
    y: Math.max(EDGE_MARGIN, (window.innerHeight - height) / 2),
    width,
    height
  };
};

const readGeometry = (storageKey?: string): Geometry => {
  if (!storageKey) return defaultGeometry();
  try {
    const raw = window.localStorage.getItem(`floating-window:${storageKey}`);
    if (!raw) return defaultGeometry();
    const parsed = JSON.parse(raw) as Partial<Geometry>;
    if (
      typeof parsed.x !== 'number' ||
      typeof parsed.y !== 'number' ||
      typeof parsed.width !== 'number' ||
      typeof parsed.height !== 'number'
    ) {
      return defaultGeometry();
    }
    // Se la finestra è stata salvata su uno schermo più grande, la riporto dentro
    const width = Math.min(Math.max(parsed.width, MIN_WIDTH), window.innerWidth - EDGE_MARGIN * 2);
    const height = Math.min(Math.max(parsed.height, MIN_HEIGHT), window.innerHeight - EDGE_MARGIN * 2);
    return { width, height, ...clampPosition(parsed.x, parsed.y, width, height) };
  } catch {
    return defaultGeometry();
  }
};

const writeGeometry = (storageKey: string | undefined, geometry: Geometry) => {
  if (!storageKey) return;
  try {
    window.localStorage.setItem(`floating-window:${storageKey}`, JSON.stringify(geometry));
  } catch {
    // localStorage non disponibile: la finestra resta comunque usabile
  }
};

export const FloatingWindow = ({
  isOpen,
  onClose,
  title,
  minimizedLabel,
  storageKey,
  headerActions,
  footer,
  children,
  className
}: FloatingWindowProps) => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);
  const [geometry, setGeometry] = useState<Geometry>(() => readGeometry(storageKey));
  const [isMaximized, setIsMaximized] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const geometryRef = useRef(geometry);
  const dragRef = useRef<{ pointerId: number; startX: number; startY: number; originX: number; originY: number } | null>(null);
  const resizeRef = useRef<{ pointerId: number; startX: number; startY: number; width: number; height: number } | null>(null);

  geometryRef.current = geometry;

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
      setGeometry((current) => {
        const width = Math.min(current.width, window.innerWidth - EDGE_MARGIN * 2);
        const height = Math.min(current.height, window.innerHeight - EDGE_MARGIN * 2);
        return { width, height, ...clampPosition(current.x, current.y, width, height) };
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Ogni apertura riparte da finestra visibile, con l'ultima geometria salvata
  useEffect(() => {
    if (!isOpen) return;
    setIsMinimized(false);
    setGeometry(readGeometry(storageKey));
  }, [isOpen, storageKey]);

  const commitGeometry = useCallback(() => {
    writeGeometry(storageKey, geometryRef.current);
  }, [storageKey]);

  const handleHeaderPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (isMobile || isMaximized) return;
    if ((event.target as HTMLElement).closest('button, input, select, textarea, a, [data-no-drag]')) return;
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: geometryRef.current.x,
      originY: geometryRef.current.y
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleHeaderPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const nextX = drag.originX + (event.clientX - drag.startX);
    const nextY = drag.originY + (event.clientY - drag.startY);
    setGeometry((current) => ({
      ...current,
      ...clampPosition(nextX, nextY, current.width, current.height)
    }));
  };

  const handlePointerEnd = (event: React.PointerEvent<HTMLElement>) => {
    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null;
      commitGeometry();
    }
    if (resizeRef.current?.pointerId === event.pointerId) {
      resizeRef.current = null;
      commitGeometry();
    }
  };

  const handleResizePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
    resizeRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      width: geometryRef.current.width,
      height: geometryRef.current.height
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleResizePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const resize = resizeRef.current;
    if (!resize || resize.pointerId !== event.pointerId) return;
    const nextWidth = resize.width + (event.clientX - resize.startX);
    const nextHeight = resize.height + (event.clientY - resize.startY);
    setGeometry((current) => ({
      ...current,
      width: Math.min(Math.max(nextWidth, MIN_WIDTH), window.innerWidth - current.x - EDGE_MARGIN),
      height: Math.min(Math.max(nextHeight, MIN_HEIGHT), window.innerHeight - current.y - EDGE_MARGIN)
    }));
  };

  if (!isOpen) return null;

  const label = minimizedLabel || (typeof title === 'string' ? title : 'Finestra');

  const windowStyle = isMobile || isMaximized
    ? undefined
    : { left: geometry.x, top: geometry.y, width: geometry.width, height: geometry.height };

  return (
    <>
      {createPortal(
        <div
          className={cn(
            'fixed z-[80] flex flex-col bg-white shadow-2xl border border-gray-200 overflow-hidden',
            isMobile ? 'inset-0 rounded-none' : isMaximized ? 'inset-3 rounded-xl' : 'rounded-xl',
            isMinimized && 'hidden',
            className
          )}
          style={windowStyle}
          role="dialog"
          aria-modal="false"
        >
          {/* Barra del titolo: trascinabile */}
          <div
            onPointerDown={handleHeaderPointerDown}
            onPointerMove={handleHeaderPointerMove}
            onPointerUp={handlePointerEnd}
            onPointerCancel={handlePointerEnd}
            onDoubleClick={() => !isMobile && setIsMaximized((value) => !value)}
            className={cn(
              'flex items-center gap-2 border-b bg-gray-50 px-2 py-1.5 select-none',
              !isMobile && !isMaximized && 'cursor-move'
            )}
          >
            <div className="flex items-center gap-2 min-w-0 flex-1 pl-1">
              <div className="min-w-0 text-sm sm:text-base font-semibold text-gray-900 truncate">{title}</div>
            </div>

            {headerActions && (
              <div className="flex flex-wrap items-center gap-1 justify-end" data-no-drag>
                {headerActions}
              </div>
            )}

            <div className="flex items-center gap-0.5 flex-shrink-0" data-no-drag>
              <button
                type="button"
                onClick={() => setIsMinimized(true)}
                title="Riduci a icona"
                className="h-7 w-7 inline-flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-200 hover:text-gray-900"
              >
                <Minus className="h-4 w-4" />
              </button>
              {!isMobile && (
                <button
                  type="button"
                  onClick={() => setIsMaximized((value) => !value)}
                  title={isMaximized ? 'Ripristina' : 'Ingrandisci'}
                  className="h-7 w-7 inline-flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-200 hover:text-gray-900"
                >
                  {isMaximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                title="Chiudi"
                className="h-7 w-7 inline-flex items-center justify-center rounded-md text-gray-500 hover:bg-red-500 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Contenuto */}
          <div className="flex-1 flex flex-col min-h-0">{children}</div>

          {footer}

          {!isMobile && !isMaximized && (
            <div
              onPointerDown={handleResizePointerDown}
              onPointerMove={handleResizePointerMove}
              onPointerUp={handlePointerEnd}
              onPointerCancel={handlePointerEnd}
              title="Ridimensiona"
              className="absolute bottom-0 right-0 h-7 w-7 cursor-nwse-resize"
            >
              <span className="absolute bottom-1.5 right-1.5 h-2.5 w-2.5 border-b-2 border-r-2 border-gray-400 rounded-sm" />
            </div>
          )}
        </div>,
        document.body
      )}

      {isMinimized &&
        createPortal(
          <div className="flex items-center gap-1.5 h-9 pl-2.5 pr-1 rounded-md border border-slate-200 bg-slate-50 hover:bg-slate-100 shadow-sm max-w-[min(18rem,calc(100vw-2rem))] transition-colors">
            <AppWindow className="h-4 w-4 flex-shrink-0 text-slate-500" />
            <button
              type="button"
              onClick={() => setIsMinimized(false)}
              className="text-xs font-medium text-slate-700 truncate"
              title={`Riapri ${label}`}
            >
              {label}
            </button>
            <button
              type="button"
              onClick={onClose}
              title="Chiudi"
              className="h-6 w-6 flex-shrink-0 inline-flex items-center justify-center rounded text-slate-400 hover:bg-slate-200 hover:text-slate-700"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>,
          getDock()
        )}
    </>
  );
};

export default FloatingWindow;
